import { useState } from "react"
import { Button } from "@/components/ui/button/button"
import { Checkbox } from "@/components/ui/checkbox/checkbox"
import { DatePicker } from "@/components/ui/date-picker/date-picker"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog/dialog"
import { Label } from "@/components/ui/label/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select/select"

interface PlanTerminationModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  propertyName: string
  totalContracts: number
}

export function PlanTerminationModal({
  open,
  onOpenChange,
  propertyName,
  totalContracts,
}: PlanTerminationModalProps) {
  const [reason, setReason] = useState("")
  const [terminationDate, setTerminationDate] = useState<Date | undefined>(undefined)
  const [consent1, setConsent1] = useState(false)
  const [consent2, setConsent2] = useState(false)
  const [consent3, setConsent3] = useState(false)

  const isFormValid = reason && terminationDate && consent1 && consent2 && consent3

  const handleComplete = () => {
    if (isFormValid) {
      console.log("Service termination submitted:", {
        reason,
        terminationDate,
        consent1,
        consent2,
        consent3,
      })
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[345px] gap-2.5 p-[25px_25px_30px] rounded-[10px]">
        <DialogHeader className="space-y-0 pb-[5px]">
          <DialogTitle className="text-center text-base font-semibold text-[#354267]">
            サービス一括終了
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-[15px]">
          <div className="bg-[#F8FAFC] rounded-md p-2.5 grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-400">物件名</p>
              <p className="text-sm font-semibold text-[#354267]">{propertyName}</p>
            </div>
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-slate-400">総契約数</p>
              <p className="text-sm font-semibold text-[#354267]">{totalContracts}件</p>
            </div>
          </div>

          <div className="space-y-2.5">
            <div className="space-y-1.5">
              <Label htmlFor="termination-reason" className="text-sm font-semibold text-slate-600">
                サービス終了理由
              </Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger
                  id="termination-reason"
                  className="h-10 border-slate-300 rounded-md px-4 text-sm text-slate-600"
                >
                  <SelectValue placeholder="理由を選択してください" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="売却">売却</SelectItem>
                  <SelectItem value="解体">解体</SelectItem>
                  <SelectItem value="自己使用">自己使用</SelectItem>
                  <SelectItem value="その他">その他</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="termination-date" className="text-sm font-semibold text-slate-600">
                サービス終了日
              </Label>
              <DatePicker
                id="termination-date"
                value={terminationDate}
                onChange={setTerminationDate}
                placeholder="日付を選択してください"
                disabled={(date) => date < new Date()}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-start gap-1.5 py-1.5">
              <Checkbox
                id="consent1"
                checked={consent1}
                onCheckedChange={(checked) => setConsent1(checked === true)}
                className="mt-0.5 h-[18px] w-[18px] rounded border-[#354267] data-[state=checked]:bg-[#354267]"
              />
              <label
                htmlFor="consent1"
                className="text-xs font-semibold text-slate-600 leading-[1.17] cursor-pointer"
              >
                本申請を持って、サービスが終了することを承認します
              </label>
            </div>

            <div className="flex items-start gap-1.5 py-1.5">
              <Checkbox
                id="consent2"
                checked={consent2}
                onCheckedChange={(checked) => setConsent2(checked === true)}
                className="mt-0.5 h-[18px] w-[18px] rounded border-[#354267] data-[state=checked]:bg-[#354267]"
              />
              <label
                htmlFor="consent2"
                className="text-xs font-semibold text-slate-600 leading-[1.17] cursor-pointer"
              >
                プラスプラン(入居者保険付き)のサービスをご解約される場合、入居者保険も終了することを承認します
                ※既存の入居者が引き続き居住される場合は、新たに入居者保険にご加入いただく必要がございます。
              </label>
            </div>

            <div className="flex items-start gap-1.5 py-1.5">
              <Checkbox
                id="consent3"
                checked={consent3}
                onCheckedChange={(checked) => setConsent3(checked === true)}
                className="mt-0.5 h-[18px] w-[18px] rounded border-[#354267] data-[state=checked]:bg-[#354267]"
              />
              <label
                htmlFor="consent3"
                className="text-xs font-semibold text-slate-600 leading-[1.17] cursor-pointer"
              >
                物件に紐づく全ての部屋の契約が終了することを承認します
              </label>
            </div>
          </div>

          <p className="text-xs font-semibold text-[#FF3232]">
            ※入居者退去の場合は、部屋詳細よりご入力ください
          </p>

          <DialogFooter className="gap-[15px] sm:justify-center">
            <Button
              onClick={handleComplete}
              disabled={!isFormValid}
              className="w-full h-auto py-1.5 px-4 bg-[#354267] hover:bg-[#354267]/90 text-sm font-bold text-white rounded-md shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]"
            >
              完了
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
